import React, { useState, useEffect, useRef } from "react";
import "./MaintenanceDetails.css";
import bellIcon from "../../../shared/assets/icons/bell.png";
import filterIcon from "../../../shared/assets/icons/filter.png";
import addIcon from "../../../shared/assets/icons/add.png";
import backIcon from "../../../shared/assets/icons/back.png";
import profileIcon from "../../../shared/assets/icons/profile.png";
import downArrow from "../../../shared/assets/icons/down.png";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import rightIcon from "../../../shared/assets/icons/rightIcon.png";
import downloadIcon from "../../../shared/assets/icons/download.png";
import { taskDetailsService } from "../services/taskDetailsService";
import StepperComponent from "../../../components/StepperComponent";
import apiClient from "../../../shared/services/apiHelper";
import {
  parseAndConvertToLocal,
  twentyFourHrFormat,
  convertMinutesToHoursAndMinutesShort,
} from "../../../shared/utils/dateFormatter";
import { formatCurrency as localFormatCurrency } from "../../../shared/utils/localeHelper";
import { useMaintenanceContext } from "../../../contexts/MaintenanceContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import HeaderNav from "../../../shared/components/HeaderNav";
import { IoArrowUndoOutline } from "react-icons/io5";
import MainLoader from "../../../shared/components/MainLoader";
import { getPriorityColor } from "../../../shared/utils/priorityHelper";
import MobileFooterNav from "../../../shared/components/MobileFooterNav";

// Add spinner animation styles
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = spinnerStyles;
  document.head.appendChild(styleSheet);
}

const MaintenanceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { maintenanceData, isLoading, fetchMaintenanceDetails } =
    useMaintenanceContext();
  const { showSnackbar } = useSnackbar();

  const serviceCategoryList = [
    { label: "PAID", value: 0 },
    { label: "UNDER WARRANTY", value: 1 },
    { label: "AMC", value: 3 },
  ];

  const getServiceCategoryLabel = (value) => {
    const category = serviceCategoryList.find((item) => item.value === value);
    return category ? category.label : "N/A";
  };

  const [confirmationSection, setConfirmationSection] = useState(false);
  const [signaturesLoading, setSignaturesLoading] = useState(false);

  // State to track status codes
  const [statusCodes, setStatusCodes] = useState([]);

  // Computed visibility states based on status codes
  const estimationSkipped = statusCodes.includes(25);
  const showPreInspection = statusCodes.includes(21);
  const showEstimation = statusCodes.includes(24);
  const showWorkOrder = statusCodes.includes(32);
  const showFeedback = statusCodes.includes(32);

  // Signature pad state
  const [isDrawing, setIsDrawing] = useState(false);

  // Loading states for confirm buttons
  const [isEstimationConfirming, setIsEstimationConfirming] = useState(false);
  const [isMobileEstimationConfirming, setIsMobileEstimationConfirming] =
    useState(false);
  const [isWorkReportConfirming, setIsWorkReportConfirming] = useState(false);
  const [isMobileWorkReportConfirming, setIsMobileWorkReportConfirming] =
    useState(false);

  const getPriorityText = (priority) => {
    switch (priority) {
      case 0: return 'LOW';
      case 1: return 'MEDIUM';
      case 2: return 'NORMAL';
      case 3: return 'HIGH';
      case 4: return 'URGENT';
      default: return 'NORMAL';
    }
  };

  const formatCurrency = (amount) => (amount !== null && amount !== undefined) ? localFormatCurrency(amount) : 'N/A';

  // Fetch status codes from task history
  useEffect(() => {
    const fetchStatusCodes = async () => {
      if (!id) return;

      try {
        const response = await taskDetailsService.getMaintenanceRequestDetails(
          id,
          "task_history",
        );

        if (response.success && response.data) {
          // Extract status codes from the history data
          const codes = response.data
            .map((item) => item.status)
            .filter((status) => status != null);
          setStatusCodes(codes);
        }
      } catch (error) {
        console.error("Error fetching status codes:", error);
      }
    };

    fetchStatusCodes();
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchMaintenanceDetails(id);
    }
  }, [id]);

  // Listen for Firebase notification events to refresh data
  useEffect(() => {
    const handleRefresh = (event) => {
      if (event.detail?.id && event.detail.id === id) {
        fetchMaintenanceDetails(id);
      }
    };

    window.addEventListener("refreshMaintenanceDetails", handleRefresh);
    return () =>
      window.removeEventListener("refreshMaintenanceDetails", handleRefresh);
  }, [id, fetchMaintenanceDetails]);

  const handleCancel = () => alert("Cancel clicked!");
  const handleSkip = () => alert("Skip clicked!");

  const handleConfirm = async () => {
    const nameInput = document.getElementById("equipment")?.value;
    const canvas = document.getElementById("signatureCanvas");

    if (!nameInput || nameInput.trim() === "") {
      showSnackbar('Please fill the required field - Name is required', 'error');
      return;
    }

    if (!canvas) {
      alert("Signature canvas not found");
      return;
    }

    // Check if canvas has signature
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((channel) => channel !== 0);

    if (!hasSignature) {
      alert(
        "Please sign the document to confirm your acceptance of the estimate",
      );
      return;
    }

    setIsEstimationConfirming(true);

    try {
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        try {
          if (!blob) {
            alert("Failed to capture signature");
            return;
          }

          const response = await apiClient.uploadSingleFile({
            endpoint: "/task/estimation-confirm",
            imageFile: blob,
            additionalData: {
              task_id: parseInt(id),
              name: nameInput.trim(),
            },
            keyName: "file",
          });

          if (response.data?.success) {
            alert("Estimation confirmed successfully!");
            setConfirmationSection(true);
            window.location.reload();
          } else {
            alert("Failed to confirm estimation");
          }
        } catch (error) {
          console.error("Error confirming estimation:", error);
          alert("Error confirming estimation");
        } finally {
          setIsEstimationConfirming(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("Error confirming estimation:", error);
      alert("Error confirming estimation");
      setIsEstimationConfirming(false);
    }
  };

  const handleMobileConfirm = async () => {
    const nameInput = document.getElementById("equipmentMobile")?.value;
    const canvas = document.getElementById("mobileSignatureCanvas");

    if (!nameInput || nameInput.trim() === "") {
      alert("Please fill the required field - Name is required");
      return;
    }

    if (!canvas) {
      alert("Signature canvas not found");
      return;
    }

    // Check if canvas has signature
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((channel) => channel !== 0);

    if (!hasSignature) {
      alert(
        "Please sign the document to confirm your acceptance of the estimate",
      );
      return;
    }

    setIsMobileEstimationConfirming(true);

    try {
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        try {
          if (!blob) {
            alert("Failed to capture signature");
            return;
          }

          const response = await apiClient.uploadSingleFile({
            endpoint: "/task/estimation-confirm",
            imageFile: blob,
            additionalData: {
              task_id: parseInt(id),
              name: nameInput.trim(),
            },
            keyName: "file",
          });

          if (response.data?.success) {
            alert("Estimation confirmed successfully!");
            setConfirmationSection(true);
            window.location.reload();
          } else {
            alert("Failed to confirm estimation");
          }
        } catch (error) {
          console.error("Error confirming estimation:", error);
          alert("Error confirming estimation");
        } finally {
          setIsMobileEstimationConfirming(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("Error confirming estimation:", error);
      alert("Error confirming estimation");
      setIsMobileEstimationConfirming(false);
    }
  };

  const handleMobileWorkReportConfirm = async () => {
    const nameInput = document.getElementById(
      "equipmentMobileWorkReport",
    )?.value;
    const canvas = document.getElementById("mobileWorkReportSignatureCanvas");

    if (!nameInput || nameInput.trim() === "") {
      alert("Please fill the required field - Name is required");
      return;
    }

    if (!canvas) {
      alert("Signature canvas not found");
      return;
    }

    // Check if canvas has signature
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((channel) => channel !== 0);

    if (!hasSignature) {
      alert(
        "Please sign the document to confirm your acceptance of the work report",
      );
      return;
    }

    setIsMobileWorkReportConfirming(true);

    try {
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        try {
          if (!blob) {
            alert("Failed to capture signature");
            return;
          }

          const response = await apiClient.uploadSingleFile({
            endpoint: "/task/confirm-report",
            imageFile: blob,
            additionalData: {
              task_id: id,
              name: nameInput.trim(),
              remarks: "",
            },
            keyName: "file",
          });

          if (response.data?.success) {
            alert("Work report confirmed successfully!");
            window.location.reload();
          } else {
            alert("Failed to confirm work report");
          }
        } catch (error) {
          console.error("Error confirming work report:", error);
          alert("Error confirming work report");
        } finally {
          setIsMobileWorkReportConfirming(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("Error confirming work report:", error);
      alert("Error confirming work report");
      setIsMobileWorkReportConfirming(false);
    }
  };

  const handleWorkReportConfirm = async () => {
    const nameInput = document.getElementById("equipment2")?.value;
    const canvas = document.getElementById("signatureCanvas2");

    if (!nameInput || nameInput.trim() === "") {
      alert("Please fill the required field - Name is required");
      return;
    }

    if (!canvas) {
      alert("Signature canvas not found");
      return;
    }

    // Check if canvas has signature
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((channel) => channel !== 0);

    if (!hasSignature) {
      alert(
        "Please sign the document to confirm your acceptance of the work report",
      );
      return;
    }

    setIsWorkReportConfirming(true);

    try {
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        try {
          if (!blob) {
            alert("Failed to capture signature");
            return;
          }

          const response = await apiClient.uploadSingleFile({
            endpoint: "/task/confirm-report",
            imageFile: blob,
            additionalData: {
              task_id: id,
              name: nameInput.trim(),
              remarks: "",
            },
            keyName: "file",
          });

          if (response.data?.success) {
            alert("Work report confirmed successfully!");
            window.location.reload();
          } else {
            alert("Failed to confirm work report");
          }
        } catch (error) {
          console.error("Error confirming work report:", error);
          alert("Error confirming work report");
        } finally {
          setIsWorkReportConfirming(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("Error confirming work report:", error);
      alert("Error confirming work report");
      setIsWorkReportConfirming(false);
    }
  };

  const [expanded, setExpanded] = useState(null);
  const status = location.state?.status || "default-status";
  const feedbackstatus = location.state?.feedbackStatus || "default-status";
  const toggleExpand = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [comments, setComments] = useState("");

  const [tags, setTags] = useState([]);
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // useEffect(() => {
  //   const fetchTags = async () => {
  //     try {
  //       const data = await taskDetailsService.fetchFeedbackTags("string", id || 0);
  //       if (data.success) {
  //         setTags(data.tags);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching tags:', error);
  //     }
  //   };
  //   fetchTags();
  // }, []);

  useEffect(() => {
    const fetchFeedbackDetails = async () => {
      try {
        const data = await taskDetailsService.getFeedbackDetails(id);
        setFeedbackDetails(data);

        if (data.success && data.data && data.data.length > 0) {
          const existingFeedback = data.data[0];
          setRating(existingFeedback.rating);
          setComments(existingFeedback.comment);
          setFeedbackSubmitted(true);
        }
      } catch (error) {
        console.error("Error fetching feedback details:", error);
      }
    };

    if (id) {
      fetchFeedbackDetails();
    }
  }, [id]);

  const handleSubmit = async () => {
    const requestBody = {
      task_id: parseInt(id),
      rating: rating,
      comment: comments,
      tags: selectedTags,
    };

    setIsSubmittingFeedback(true);

    try {
      const data = await taskDetailsService.submitFeedback(requestBody);
      if (data.success) {
        setFeedbackSubmitted(true);
      } else {
        alert("Error submitting feedback!!");
      }
    } catch (error) {
      alert("Error submitting feedback!!");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const [currentPage, setCurrentPage] = React.useState(1);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleTagClick = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      setComments((prev) => (prev ? `${prev}, ${tag}` : tag));
    } else {
      const updatedTags = selectedTags.filter((t) => t !== tag);
      setSelectedTags(updatedTags);

      const updatedComments = comments
        .split(", ")
        .filter((text) => text !== tag)
        .join(", ");
      setComments(updatedComments);
    }
  };

  // Handle report generation
  const handleReportGeneration = async () => {
    try {
      const pdfUrl = await taskDetailsService.generateWorkReport(
        maintenanceData?.id?.toString() || "",
        maintenanceData?.equipment?.id?.toString() || "",
      );

      if (pdfUrl) {
        navigate("/pdf-view", { state: { pdfUrl, title: "Work Report" } });
      } else {
        alert("Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report");
    }
  };

  // Handle invoice generation
  const handleInvoiceGeneration = async () => {
    const estimationSkipped = statusCodes.includes(25);
    const workReportCompleted = statusCodes.includes(32);
    const estimationConfirmed = !!maintenanceData?.estimation?.confirmed_by;
    if (!estimationSkipped && !estimationConfirmed) {
      showSnackbar('Invoice unavailable: estimation has not been confirmed yet.', 'error');
      return;
    }
    if (!workReportCompleted) {
      showSnackbar('Invoice unavailable: work report is not completed yet.', 'error');
      return;
    }
    try {
      const pdfUrl = await taskDetailsService.generateInvoice(
        maintenanceData?.id?.toString() || "",
      );
      if (pdfUrl && pdfUrl !== "000") {
        navigate("/pdf-view", { state: { pdfUrl, title: "Invoice" } });
      } else {
        showSnackbar('Invoice is not available yet.', 'error');
      }
    } catch (error) {
      console.error("", error);
      showSnackbar('Error generating invoice.', 'error');
    }
  };

  // Signature pad functions - FIXED VERSION
  const setupCanvas = (canvas, isReadOnly = false) => {
    if (!canvas) {
      console.error("Canvas element not found for setup");
      return;
    }

    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Could not get 2d context from canvas");
        return;
      }

      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";

      // Set cursor and pointer events based on whether canvas is read-only
      if (isReadOnly) {
        canvas.style.cursor = "default";
        canvas.style.pointerEvents = "none";
      } else {
        canvas.style.cursor = "crosshair";
        canvas.style.pointerEvents = "auto";
      }
      canvas.style.touchAction = "none"; // Prevent scrolling on touch
      canvas.style.userSelect = "none"; // Prevent text selection
    } catch (error) {
      console.error("Error setting up canvas:", error);
    }
  };

  const startDrawing = (e, canvas) => {
    if (e.button && e.button !== 0) return; // Only left mouse button
    e.preventDefault();
    e.stopPropagation();

    setIsDrawing(true);

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ctx = canvas.getContext("2d");

    // Start a new path - this is crucial to prevent old drawings from reappearing
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e, canvas) => {
    if (!isDrawing) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ctx = canvas.getContext("2d");

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDrawing(false);
  };

  const clearSignature = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // Clear the canvas completely
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Reset the context state to prevent old paths from reappearing
    ctx.beginPath();

    // Ensure drawing is stopped
    setIsDrawing(false);
  };

  // Load signature image into canvas when component mounts or data changes
  useEffect(() => {
    const loadSignatureImage = (canvasId, imageUrl) => {
      if (!imageUrl) {
        return Promise.resolve();
      }

      const canvas = document.getElementById(canvasId);
      if (!canvas) {
        return Promise.resolve();
      }

      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.crossOrigin = "anonymous";

      return new Promise((resolve) => {
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.beginPath(); // Reset path state
          ctx.drawImage(img, 5, 5, canvas.width - 10, canvas.height - 10);
          resolve();
        };

        img.onerror = (error) => {
          console.error(
            `Failed to load signature image for ${canvasId}:`,
            error,
          );
          resolve();
        };

        img.src = imageUrl;
      });
    };

    const loadSignatures = async () => {
      if (!maintenanceData) return;

      setSignaturesLoading(true);

      try {
        const promises = [];

        // Load estimation signature if exists
        if (maintenanceData?.estimationSignature?.length > 0) {
          promises.push(
            loadSignatureImage(
              "signatureCanvas",
              maintenanceData.estimationSignature[0],
            ),
          );
          promises.push(
            loadSignatureImage(
              "mobileSignatureCanvas",
              maintenanceData.estimationSignature[0],
            ),
          );
        } else {
          // Clear canvas if no signature
          const canvas1 = document.getElementById("signatureCanvas");
          const canvas2 = document.getElementById("mobileSignatureCanvas");
          if (canvas1) {
            const ctx1 = canvas1.getContext("2d");
            ctx1.clearRect(0, 0, canvas1.width, canvas1.height);
            ctx1.beginPath();
          }
          if (canvas2) {
            const ctx2 = canvas2.getContext("2d");
            ctx2.clearRect(0, 0, canvas2.width, canvas2.height);
            ctx2.beginPath();
          }
        }

        // Load work report signature if exists
        if (maintenanceData?.serviceReportSignature?.length > 0) {
          promises.push(
            loadSignatureImage(
              "signatureCanvas2",
              maintenanceData.serviceReportSignature[0],
            ),
          );
          promises.push(
            loadSignatureImage(
              "mobileWorkReportSignatureCanvas",
              maintenanceData.serviceReportSignature[0],
            ),
          );
        } else {
          // Clear canvas if no signature
          const canvas = document.getElementById("signatureCanvas2");
          const mobileCanvas = document.getElementById(
            "mobileWorkReportSignatureCanvas",
          );
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.beginPath();
          }
          if (mobileCanvas) {
            const ctx = mobileCanvas.getContext("2d");
            ctx.clearRect(0, 0, mobileCanvas.width, mobileCanvas.height);
            ctx.beginPath();
          }
        }

        await Promise.all(promises);
      } finally {
        setSignaturesLoading(false);
      }
    };

    // Delay to ensure DOM is ready
    const timeoutId = setTimeout(loadSignatures, 300);
    return () => clearTimeout(timeoutId);
  }, [maintenanceData]);

  useEffect(() => {
    // Setup all signature canvases with delay to ensure DOM is ready
    const setupCanvases = () => {
      const canvasIds = [
        "signatureCanvas",
        "signatureCanvas2",
        "mobileSignatureCanvas",
        "mobileWorkReportSignatureCanvas",
      ];
      const eventListeners = [];

      canvasIds.forEach((canvasId) => {
        const canvas = document.getElementById(canvasId);
        if (!canvas) {
          return;
        }

        // Determine if this canvas should be interactive based on signature data
        let isReadOnly = false;
        if (
          canvasId === "signatureCanvas" ||
          canvasId === "mobileSignatureCanvas"
        ) {
          isReadOnly = maintenanceData?.estimationSignature?.length > 0;
        } else if (
          canvasId === "signatureCanvas2" ||
          canvasId === "mobileWorkReportSignatureCanvas"
        ) {
          isReadOnly = maintenanceData?.serviceReportSignature?.length > 0;
        }

        setupCanvas(canvas, isReadOnly);

        if (isReadOnly) {
          return;
        }

        const handleMouseDown = (e) => {
          if (e.button !== 0) return; // Only left mouse button
          startDrawing(e, canvas);
        };

        const handleMouseMove = (e) => {
          draw(e, canvas);
        };

        const handleMouseUp = (e) => {
          stopDrawing(e);
        };

        const handleMouseOut = (e) => {
          // Only stop drawing if mouse actually leaves the canvas
          const rect = canvas.getBoundingClientRect();
          const x = e.clientX;
          const y = e.clientY;

          if (
            x < rect.left ||
            x > rect.right ||
            y < rect.top ||
            y > rect.bottom
          ) {
            stopDrawing(e);
          }
        };

        // Touch events for mobile
        const handleTouchStart = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const touch = e.touches[0];
          setIsDrawing(true);
          const rect = canvas.getBoundingClientRect();
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          const ctx = canvas.getContext("2d");
          ctx.beginPath();
          ctx.moveTo(x, y);
        };

        const handleTouchMove = (e) => {
          if (!isDrawing) return;
          e.preventDefault();
          e.stopPropagation();
          const touch = e.touches[0];
          const rect = canvas.getBoundingClientRect();
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          const ctx = canvas.getContext("2d");
          ctx.lineTo(x, y);
          ctx.stroke();
        };

        const handleTouchEnd = (e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDrawing(false);
        };

        canvas.addEventListener("mousedown", handleMouseDown, {
          passive: false,
        });
        canvas.addEventListener("mousemove", handleMouseMove, {
          passive: false,
        });
        canvas.addEventListener("mouseup", handleMouseUp, { passive: false });
        canvas.addEventListener("mouseleave", handleMouseOut, {
          passive: false,
        });
        canvas.addEventListener("touchstart", handleTouchStart, {
          passive: false,
        });
        canvas.addEventListener("touchmove", handleTouchMove, {
          passive: false,
        });
        canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
        canvas.addEventListener("touchcancel", handleTouchEnd, {
          passive: false,
        });

        // Store listeners for cleanup
        eventListeners.push({
          canvas,
          listeners: [
            { event: "mousedown", handler: handleMouseDown },
            { event: "mousemove", handler: handleMouseMove },
            { event: "mouseup", handler: handleMouseUp },
            { event: "mouseleave", handler: handleMouseOut },
            { event: "touchcancel", handler: handleTouchEnd },
            { event: "touchstart", handler: handleTouchStart },
            { event: "touchmove", handler: handleTouchMove },
            { event: "touchend", handler: handleTouchEnd },
          ],
        });
      });

      return eventListeners;
    };

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const eventListeners = setupCanvases();

      // Cleanup function
      return () => {
        eventListeners.forEach(({ canvas, listeners }) => {
          listeners.forEach(({ event, handler }) => {
            canvas.removeEventListener(event, handler);
          });
        });
      };
    }, 250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [maintenanceData, isDrawing]);

  // Loading component
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F6F8FC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MainLoader />
      </div>
    );
  }

  return (
    <>
      <div className="maintenance-request">
        <HeaderNav profileIcon={profileIcon} />
        {/* Header Section */}
        <div className="second-header">
          <button className="back-button">
            <IoArrowUndoOutline
              color="black"
              size={25}
              alt="Back"
              onClick={() => navigate("/tasklist")}
            />
          </button>
          <h2 className="page-title">Maintenance Details</h2>
        </div>
        {/* Mobile Header */}
        <header className="mobile-header">
          <div className="header-left">
            <IoArrowUndoOutline
              color="black"
              size={25}
              alt="Back"
              onClick={() => navigate("/tasklist")}
            />
          </div>
          <h1 className="app-title">Maintenance Details</h1>
        </header>

        <div className="workorder-container">
          {/* Main product card */}
          <div className="product-card">
          <div className="product-title-row">
  <div className="product-name-row">
    {maintenanceData?.equipment?.equipmentName && (
      <span className="product-name">
        {maintenanceData.equipment.equipmentName}
      </span>
    )}

    {maintenanceData?.equipment?.equipmentName &&
      maintenanceData?.equipment?.product && (
        <span className="product-make">
          &nbsp;| {maintenanceData.equipment.product}
        </span>
      )}

    {!maintenanceData?.equipment?.equipmentName &&
      maintenanceData?.equipment?.product && (
        <span className="product-make">
          {maintenanceData.equipment.product}
        </span>
      )}
  </div>
</div>

            <div className="status-warranty-row">
              <span className="status ongoing" style={(() => {
                const p = maintenanceData?.schedule?.priorityRaw;
                const color = getPriorityColor(p);
                return { background: color + '22', color, borderColor: color + '66' };
              })()}>
                {getPriorityText(maintenanceData?.schedule?.priorityRaw) || ""}
              </span>

              <span className="warranty">
                {maintenanceData?.equipment?.model}
                {maintenanceData?.equipment?.model && getServiceCategoryLabel(maintenanceData?.warrantyType) ? ` | ${getServiceCategoryLabel(maintenanceData?.warrantyType)}` : getServiceCategoryLabel(maintenanceData?.warrantyType) || ''}
              </span>

              <span className="warranty">
                <strong>Request ID:</strong> {maintenanceData?.reqId || "N/A"}
              </span>
            </div>

            {/* <div className="issue-description">
              {maintenanceData?.issues?.details?.join(", ") ||
                "No issues reported"}
            </div> */}

            <div className="date-price-row">
              <div className="reported-date">
                <span className="label">Reported On</span>
                <span className="value">
                  {maintenanceData?.reportedDate
                    ? twentyFourHrFormat(
                        parseAndConvertToLocal(maintenanceData.reportedDate),
                        true,
                      )
                    : "N/A"}
                </span>
              </div>
              <div className="total-price">
                {showWorkOrder && maintenanceData?.workReport?.totalAmount ? (
                  <>
                    <span className="label">Invoice Amount</span>
                    <span className="value">{formatCurrency(maintenanceData.workReport.totalAmount)}</span>
                  </>
                ) : showEstimation && maintenanceData?.estimation?.totalAmount ? (
                  <>
                    <span className="label">Estimated Amount</span>
                    <span className="value">{formatCurrency(maintenanceData.estimation.totalAmount)}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* service status header with action links */}
          <div className="service-status-header">
            <h3>Your Service Status</h3>
            <div className="action-links">
              <span onClick={() => navigate(`/tasklist?equipment_id=${maintenanceData?.equipment?.id}`)} style={{cursor:'pointer'}}>View History</span> <span>|</span> <span onClick={() => navigate(`/checklist/${id}`)} style={{cursor:'pointer'}}>Checklist</span>{" "}
           <span>|</span> 
               {/*   <span>Estimation</span> <span>|</span>{" "} */}
              <span onClick={handleInvoiceGeneration}>Invoice</span>
            </div>
          </div>

          {/* Dynamic Stepper */}
          <StepperComponent
            taskId={id}
            isMobile={false}
            reportedDate={maintenanceData?.reportedDate}
            maintenanceData={maintenanceData}
            statusCodes={statusCodes}
            signaturesLoading={signaturesLoading}
            onEstimationConfirm={async (blob, name) => {
              setIsEstimationConfirming(true);
              try {
                const response = await apiClient.uploadSingleFile({ endpoint: '/task/estimation-confirm', imageFile: blob, additionalData: { task_id: parseInt(id), name }, keyName: 'file' });
                if (response.data?.success) { alert('Estimation confirmed successfully!'); window.location.reload(); }
                else alert(response.data?.message || 'Failed to confirm estimation');
              } catch (e) { alert(e?.response?.data?.message || 'Error confirming estimation'); }
              finally { setIsEstimationConfirming(false); }
            }}
            onWorkReportConfirm={async (blob, name) => {
              setIsWorkReportConfirming(true);
              try {
                const response = await apiClient.uploadSingleFile({ endpoint: '/task/confirm-report', imageFile: blob, additionalData: { task_id: parseInt(id), name, remarks: '' }, keyName: 'file' });
                if (response.data?.success) { alert('Work report confirmed successfully!'); window.location.reload(); }
                else alert('Failed to confirm work report');
              } catch (e) {
                console.error('Work report confirm error:', e);
                alert('Error confirming work report');
              } finally { setIsWorkReportConfirming(false); }
            }}
            onFeedbackSubmit={async ({ rating, comment, tags }) => {
              const data = await taskDetailsService.submitFeedback({ task_id: parseInt(id), rating, comment, tags });
              if (data.success) return true;
              alert('Error submitting feedback');
              return false;
            }}
          />
        </div>
          <MobileFooterNav />
      </div>

      {/* Mobile screen details */}
      {false && (
      <div className="maintenance-details-mobile">
        {/* Second Header */}
        <header
          className="mobile-header"
          style={{ backgroundColor: "#202941" }}
        >
          <div className="back-button" onClick={() => navigate("/tasklist")}>
            <img src={backIcon} alt="Back" className="icon back-icon" />
          </div>
          <div className="main-mobile-header">
            <h2 className="main-title montserrat-20">WO Details</h2>
            <p className="subtext montserrat-10">
              {maintenanceData?.reqId || "N/A"} |{" "}
              {maintenanceData?.companyName || "N/A"}|{" "}
              {maintenanceData?.equipment?.productName || "N/A"}
            </p>
          </div>
        </header>

        <StepperComponent
          taskId={id}
          isMobile={true}
          reportedDate={maintenanceData?.reportedDate}
        />

        {showFeedback && (
          <div className="mobile-button">
            {/* Report Button */}
            <button className="report-button" onClick={handleReportGeneration}>
              Report
              <img src={downloadIcon} alt="Download" />
            </button>

            {/* Invoice Button */}
            <button
              className="invoice-button"
              onClick={handleInvoiceGeneration}
            >
              Invoice
              <img src={downloadIcon} alt="Download" />
            </button>
          </div>
        )}

        {/* Expandable Sections */}
        <div
          className="card montserrat-16"
          style={{ margin: "0px", marginTop: "20px" }}
        >
          <div
            className="card-header"
            onClick={() => toggleExpand("technician")}
          >
            {maintenanceData?.companyName || "N/A"}
            <span style={{ marginLeft: "auto", marginRight: "10px" }}>
              {maintenanceData?.reqId || "N/A"}
            </span>
            <span>
              {expanded === "technician" ? (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              ) : (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              )}
            </span>
          </div>
          {expanded === "technician" && (
            <div className="card-content">
              <p>{maintenanceData?.companyName || "N/A"}</p>
              <br />
              <p>{maintenanceData?.phoneNumber || "N/A"}</p>
              <br />
              <p>{maintenanceData?.email || "N/A"}</p>
              <br />
              <p>{maintenanceData?.address?.fullAddress || "N/A"}</p>
            </div>
          )}
        </div>

        <div
          className="card montserrat-16"
          style={{ margin: "0px", marginTop: "20px" }}
        >
          <div
            className="card-header"
            onClick={() => toggleExpand("airconditioner")}
          >
            {maintenanceData?.equipment?.productName || "Equipment"}{" "}
            <span>
              {expanded === "airconditioner" ? (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              ) : (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              )}
            </span>
          </div>
          {expanded === "airconditioner" && (
            <div className="card-content">
              <p> {maintenanceData?.equipment?.make || "N/A"}</p>
              <br />
              <p> {maintenanceData?.equipment?.model || "N/A"}</p>
              <br />

              <p>
                Contract Type:{" "}
                <span className="">
                  {getServiceCategoryLabel(maintenanceData?.warrantyType)}
                </span>
              </p>
              <br />
            </div>
          )}
        </div>

        <div
          className="card montserrat-16"
          style={{ margin: "0px", marginTop: "20px" }}
        >
          <div className="card-header" onClick={() => toggleExpand("issues")}>
            Reported Issues{" "}
            <span>
              {expanded === "issues" ? (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              ) : (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              )}
            </span>
          </div>
          {expanded === "issues" && (
            <div className="card-content">
              <ol className="montserrat-14" style={{ padding: "10px" }}>
                {maintenanceData?.issues?.details?.map((issue, index) => (
                  <li key={index}>
                    <p>{`${issue}`}</p>
                    <br />
                  </li>
                )) || <li>No issues reported</li>}
              </ol>
            </div>
          )}
        </div>

        <div
          className="card montserrat-16"
          style={{ margin: "0px", marginTop: "20px" }}
        >
          <div className="card-header" onClick={() => toggleExpand("details")}>
            Details{" "}
            <span>
              {expanded === "details" ? (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              ) : (
                <img
                  src={downArrow}
                  style={{ width: "25px", height: "15px", marginTop: "10px" }}
                  alt="Back"
                  className="icon back-icon"
                />
              )}
            </span>
          </div>
          {expanded === "details" && (
            <div className="card-content">
              <br />
              <p>
                <b>Priority:</b> {maintenanceData?.schedule?.priorityRaw}
              </p>
              <br />
              <p>
                <b>Scheduled Date:</b>{" "}
                {maintenanceData.schedule.scheduledDate &&
                maintenanceData.schedule.scheduledDate !== "N/A"
                  ? twentyFourHrFormat(
                      maintenanceData.schedule.scheduledDate,
                      true,
                    )
                  : "N/A"}
              </p>
              <br />
              <p>
                <b>Reported Date:</b>{" "}
                {twentyFourHrFormat(
                  parseAndConvertToLocal(maintenanceData.reportedDate),
                  true,
                )}
              </p>
              <br />
              <p>
                <b>Duration:</b> {maintenanceData.schedule.taskDuration}
              </p>
              <br />
              <p>
                <b>Travel Time:</b> {maintenanceData.schedule.travelTime}
              </p>
              <br />
              <p>
                <b>Technician:</b> {maintenanceData.technician.name}
              </p>
              <br />
            </div>
          )}
        </div>

        {/* Findings & Recommendations with Inspection Photos merged */}
        {showPreInspection && maintenanceData && (
          <>
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("findings")}
              >
                Findings & Recommendations{" "}
                <span>
                  <img src={downArrow} style={{ width: "25px", height: "15px", marginTop: "10px" }} alt="Back" className="icon back-icon" />
                </span>
              </div>
              {expanded === "findings" && (
                <div className="card-content">
                  <div className="montserrat-14" style={{ padding: "10px" }}>
        {/* Comments with images */}
                    {maintenanceData?.inspectionComments?.length > 0 ? (
                      maintenanceData.inspectionComments.map((item, index) => (
                        <div key={index} style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "10px", padding: "10px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e8e8e8" }}>
                          <div style={{ minWidth: "24px", height: "24px", borderRadius: "50%", background: "#35605a", color: "#fff", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 600 }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            {item.comments?.trim() && (
                              <p style={{ margin: "0 0 8px", fontSize: "13px", color: "#333", lineHeight: "1.6" }}>{item.comments}</p>
                            )}
                            {item.file && (
                              <img src={item.file} alt={`Inspection ${index + 1}`} style={{ width: "100%", maxWidth: "200px", height: "140px", objectFit: "cover", borderRadius: "6px", border: "1px solid #ddd" }} onError={e => e.target.style.display = "none"} />
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: "#888", fontSize: "13px", marginBottom: "12px" }}>No findings or recommendations available</p>
                    )}

                    {/* Inspection Photos without comment */}
                    {maintenanceData?.inspectionFiles?.filter(url => !maintenanceData?.inspectionComments?.some(c => c.file === url)).length > 0 && (
                      <>
                        <p style={{ fontWeight: 600, fontSize: "13px", color: "#374151", margin: "12px 0 8px" }}>Inspection Photos</p>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, 150px)", gap: "6px", justifyContent: "start" }}>
                          {maintenanceData.inspectionFiles.filter(url => !maintenanceData?.inspectionComments?.some(c => c.file === url)).map((imageUrl, index) => (
                            <div key={index} style={{ border: "1px solid #ddd", borderRadius: "5px", overflow: "hidden" }}>
                              <img src={imageUrl} alt={`Inspection ${index + 1}`} style={{ width: "100%", height: "150px", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Estimated Details - Show only if status 24 exists */}
        {showEstimation && maintenanceData?.estimation && (
          <>
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("estimate")}
              >
                Estimated Details
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    marginLeft: "auto",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", fontWeight: "300" }}>
                    Total
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: "500" }}>
                    {formatCurrency(maintenanceData.estimation.totalAmount)}
                  </span>
                </div>
                <span>
                  {expanded === "estimate" ? (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "estimate" && (
                <div className="card-content">
                  <br />
                  <p>
                    <b>Labour Cost:</b> {formatCurrency(maintenanceData.estimation.labour_cost?.totalLabourCost)}
                  </p>
                  <p>
                    <b>Duration:</b>{" "}
                    {convertMinutesToHoursAndMinutesShort(
                      maintenanceData.estimation.labour_cost?.durationInMinutes,
                    ) || "N/A"}
                  </p>
                  <p>
                    <b>Rate:</b> {formatCurrency(maintenanceData.estimation.labour_cost?.rate)}
                  </p>
                  <br />
                  <p>
                    <b>Spare Parts:</b>
                  </p>
                  {maintenanceData.estimation.spare_parts_cost?.length > 0 ? (
                    maintenanceData.estimation.spare_parts_cost.map(
                      (part, index) => (
                        <div
                          key={index}
                          style={{ marginLeft: "10px", marginBottom: "5px" }}
                        >
                          <p>
                          • {part?.name || "N/A"} - Qty: {part?.qty || "N/A"}{" "}
                          - {formatCurrency(part?.totalSpareCost)}
                        </p>
                        </div>
                      ),
                    )
                  ) : (
                    <p></p>
                  )}
                  <br />
                  <hr
                    style={{ border: "0.1px solid #FFFFFF", margin: "10px 0" }}
                  />
                  <p>
                    <b>
                      Grand Total: {formatCurrency(maintenanceData.estimation.totalAmount)}
                    </b>
                  </p>
                  {maintenanceData.estimation.confirmed_by && (
                    <p>
                      <b>Confirmed By:</b>{" "}
                      {maintenanceData.estimation.confirmed_by}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Estimation Signature - Show right after estimation details */}
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <p>
                {maintenanceData?.estimationSignature?.length > 0
                  ? "Estimation Confirmed"
                  : "If you agree with the estimation, please sign and confirm"}
              </p>
              {!(maintenanceData?.estimationSignature?.length > 0) && (
                <div className="form-group">
                  <input
                    type="text"
                    id="equipmentMobile"
                    placeholder="Name*"
                    className="montserrat-input"
                    style={{ width: "100%", height: "30px", marginTop: "20px" }}
                  />
                </div>
              )}
              <div className="signature-pad" style={{ position: "relative" }}>
                <canvas
                  id="mobileSignatureCanvas"
                  width="400"
                  height="200"
                  style={{
                    border: "1px solid #000",
                    backgroundColor: "white",
                    borderRadius: "5px",
                  }}
                ></canvas>
                {signaturesLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        border: "3px solid #f3f3f3",
                        borderTop: "3px solid #3498db",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                    <MainLoader />
                  </div>
                )}
              </div>
              {!(maintenanceData?.estimationSignature?.length > 0) && (
                <div className="action-buttons" style={{ marginTop: "10px" }}>
                  <button
                    className="cancel-btn"
                    onClick={() => clearSignature("mobileSignatureCanvas")}
                    style={{ marginRight: "10px" }}
                  >
                    Clear
                  </button>
                  {isMobileEstimationConfirming ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100px",
                        height: "50px",
                      }}
                    >
                      <div
                        style={{
                          border: "3px solid #f3f3f3",
                          borderTop: "3px solid #3498db",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                    </div>
                  ) : (
                    <button
                      className="confirm-btn"
                      onClick={handleMobileConfirm}
                    >
                      Confirm
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Work Done Comments - Show only if status 32 exists */}
        {showWorkOrder && (
          <>
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("workreportphotos")}
              >
                Work Report Photos / Videos{" "}
                <span>
                  {expanded === "workreportphotos" ? (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "workreportphotos" && (
                <div className="card-content">
                  <div
                    className="photo-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "5px",
                      marginTop: "10px",
                    }}
                  >
                    {maintenanceData.workReportFiles &&
                    maintenanceData.workReportFiles.length > 0 ? (
                      maintenanceData.workReportFiles.map((imageUrl, index) => (
                        <div
                          key={index}
                          className="photo"
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Work report ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                          <div
                            style={{
                              display: "none",
                              height: "150px",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f5f5f5",
                              color: "#666",
                            }}
                          >
                            Image not available
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          gridColumn: "1 / -1",
                          textAlign: "center",
                          color: "#666",
                          padding: "20px",
                        }}
                      >
                        No work report images available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("workdone")}
              >
                Work Done{" "}
                <span>
                  {expanded === "workdone" ? (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "workdone" && (
                <div className="card-content">
                  <ol className="montserrat-14" style={{ padding: "10px" }}>
                    {maintenanceData?.workDoneComments?.length > 0 ? (
                      maintenanceData.workDoneComments.map((comment, index) => (
                        <li key={index}>
                          <p>{typeof comment === 'object' ? comment.comments : comment}</p>
                          {typeof comment === 'object' && comment.file && (
                            <img src={comment.file} alt={`Work ${index + 1}`} style={{ maxWidth: '100%', borderRadius: '5px', marginBottom: '8px' }} />
                          )}
                          <br />
                        </li>
                      ))
                    ) : (
                      <li>No work done comments available</li>
                    )}
                  </ol>
                </div>
              )}
            </div>
          </>
        )}

        {/* Spare Parts - Show only if status 32 exists */}
        {showWorkOrder && (
          <>
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("spare")}
              >
                Spare Parts
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    marginLeft: "auto",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", fontWeight: "300" }}>
                    Total
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: "500" }}>
                    {formatCurrency(
                      Array.isArray(
                        maintenanceData?.workReport?.spare_parts_cost,
                      )
                        ? maintenanceData.workReport.spare_parts_cost.reduce(
                            (total, part) =>
                              total + (part?.totalSpareCost || 0),
                            0,
                          )
                        : null,
                    )}
                  </span>
                </div>
                <span style={{ margin: "0 5px" }}>
                  {expanded === "spare" ? (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "spare" && (
                <div className="card-content">
                  <div className="spare-parts-table">
                    <br />
                    <div className="row header">
                      <span>Details</span>
                      <span>Cost</span>
                    </div>
                    {Array.isArray(
                      maintenanceData?.workReport?.spare_parts_cost,
                    ) &&
                    maintenanceData.workReport.spare_parts_cost.length > 0 ? (
                      maintenanceData.workReport.spare_parts_cost.map(
                        (part, index) => (
                          <div key={index} className="row header">
                            <span>
                              {part?.name || "N/A"} (Qty: {part?.qty || "N/A"})
                            </span>
                            <span>{formatCurrency(part?.totalSpareCost)}</span>
                          </div>
                        ),
                      )
                    ) : (
                      <div className="row header">
                        <span></span>
                        <span>{formatCurrency(0)}</span>
                      </div>
                    )}
                    <hr
                      style={{
                        border: "0.1px solid #FFFFFF",
                        margin: "10px 0",
                      }}
                    />
                    <div className="row header">
                      <h3>Total</h3>
                      <h3>
                        {formatCurrency(
                          Array.isArray(
                            maintenanceData?.workReport?.spare_parts_cost,
                          )
                            ? maintenanceData.workReport.spare_parts_cost.reduce(
                                (total, part) =>
                                  total + (part?.totalSpareCost || 0),
                                0,
                              )
                            : 0,
                        )}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("labour")}
              >
                Labour{" "}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    marginLeft: "auto",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", fontWeight: "300" }}>
                    Total
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: "500" }}>
                    {formatCurrency(
                      maintenanceData?.workReport?.labour_cost?.totalLabourCost,
                    )}
                  </span>
                </div>
                <span style={{ margin: "0 5px" }}>
                  {expanded === "labour" ? (
                    <img
                      src={downArrow}
                      style={{ width: "25px", height: "15px" }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{ width: "25px", height: "15px" }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "labour" && (
                <div className="card-content">
                  <div className="spare-parts-table">
                    <br />
                    <div className="row header">
                      <span>
                        Duration:{" "}
                        {convertMinutesToHoursAndMinutesShort(
                          maintenanceData?.workReport?.labour_cost
                            ?.durationInMinutes,
                        ) || "N/A"}{" "}
                      </span>
                      <span>
                        Cost:{" "}
                        {formatCurrency(
                          maintenanceData?.workReport?.labour_cost
                            ?.totalLabourCost,
                        )}
                      </span>
                    </div>
                    <hr
                      style={{
                        border: "0.1px solid #FFFFFF",
                        margin: "10px 0",
                      }}
                    />
                    <div className="row header">
                      <h3>Total</h3>
                      <h3>
                        {formatCurrency(
                          maintenanceData?.workReport?.labour_cost
                            ?.totalLabourCost,
                        )}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Work Report Details - Show only if status 32 exists */}
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <div
                className="card-header"
                onClick={() => toggleExpand("workreport")}
              >
                Work Report Details
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    marginLeft: "auto",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", fontWeight: "300" }}>
                    Total
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: "500" }}>
                    {formatCurrency(maintenanceData?.workReport?.totalAmount)}
                  </span>
                </div>
                <span style={{ margin: "0 5px" }}>
                  {expanded === "workreport" ? (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  ) : (
                    <img
                      src={downArrow}
                      style={{
                        width: "25px",
                        height: "15px",
                        marginTop: "10px",
                      }}
                      alt="Back"
                      className="icon back-icon"
                    />
                  )}
                </span>
              </div>
              {expanded === "workreport" && (
                <div className="card-content">
                  <br />
                  <p>
                    <b>Spare Parts Cost:</b>{" "}
                    {formatCurrency(
                      Array.isArray(
                        maintenanceData?.workReport?.spare_parts_cost,
                      )
                        ? maintenanceData.workReport.spare_parts_cost.reduce(
                            (total, part) =>
                              total + (part?.totalSpareCost || 0),
                            0,
                          )
                        : null,
                    )}
                  </p>
                  <br />
                  <p>
                    <b>Labour Cost:</b>{" "}
                    {formatCurrency(
                      maintenanceData?.workReport?.labour_cost?.totalLabourCost,
                    )}
                  </p>
                  <p>
                    <b>Duration:</b>{" "}
                    {convertMinutesToHoursAndMinutesShort(
                      maintenanceData?.workReport?.labour_cost
                        ?.durationInMinutes,
                    ) || "N/A"}
                  </p>
                  <br />
                  <hr
                    style={{ border: "0.1px solid #FFFFFF", margin: "10px 0" }}
                  />
                  <p>
                    <b>
                      Grand Total:{" "}
                      {formatCurrency(maintenanceData?.workReport?.totalAmount)}
                    </b>
                  </p>
                </div>
              )}
            </div>

            {/* Work Report Signature - Show only if status 32 exists */}
            <div
              className="card montserrat-16"
              style={{ margin: "0px", marginTop: "20px" }}
            >
              <p>
                {maintenanceData?.serviceReportSignature?.length > 0
                  ? "Work Report Confirmed"
                  : "If you agree with the work report, please sign and confirm"}
              </p>
              {!(maintenanceData?.serviceReportSignature?.length > 0) && (
                <div className="form-group">
                  <input
                    type="text"
                    id="equipmentMobileWorkReport"
                    placeholder="Name*"
                    className="montserrat-input"
                    style={{ width: "100%", height: "30px", marginTop: "20px" }}
                  />
                </div>
              )}
              <div className="signature-pad" style={{ position: "relative" }}>
                <canvas
                  id="mobileWorkReportSignatureCanvas"
                  width="400"
                  height="200"
                  style={{
                    border: "1px solid #000",
                    backgroundColor: "white",
                    borderRadius: "5px",
                  }}
                ></canvas>
                {signaturesLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        border: "3px solid #f3f3f3",
                        borderTop: "3px solid #3498db",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                   <MainLoader />
                  </div>
                )}
              </div>
              {!(maintenanceData?.serviceReportSignature?.length > 0) && (
                <div className="action-buttons" style={{ marginTop: "10px" }}>
                  <button
                    className="cancel-btn"
                    onClick={() =>
                      clearSignature("mobileWorkReportSignatureCanvas")
                    }
                    style={{ marginRight: "10px" }}
                  >
                    Clear
                  </button>
                  {isMobileWorkReportConfirming ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100px",
                        height: "50px",
                      }}
                    >
                      <div
                        style={{
                          border: "3px solid #f3f3f3",
                          borderTop: "3px solid #3498db",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                    </div>
                  ) : (
                    <button
                      className="confirm-btn"
                      onClick={handleMobileWorkReportConfirm}
                    >
                      Confirm
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Feedback Section - Show only when work order is completed */}
        {showFeedback && (
          <div
            className="feedback-form"
            backgroundColor="#202941"
            style={{ marginTop: "20px" }}
          >
            {feedbackSubmitted ? (
              <>
                <h2>Thank you for your feedback!</h2>
                <div className="stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className={star <= rating ? "active" : ""} style={{ cursor: "default" }}>★</span>
                  ))}
                </div>
                {selectedTags.length > 0 && (
                  <div className="suggestions">
                    {selectedTags.map((tag) => (
                      <button key={tag} className="active" disabled>{tag}</button>
                    ))}
                  </div>
                )}
                {comments && <p style={{ marginTop: "10px" }}>{comments}</p>}
              </>
            ) : (
              <>
                <h2>How would you rate your experience?</h2>
                <div className="stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={star <= rating ? "active" : ""}
                      onClick={() => setRating(star)}
                      style={{ cursor: "pointer" }}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <h3>Let us know your suggestions:</h3>
                <div className="suggestions">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      className={selectedTags.includes(tag) ? "active" : ""}
                      onClick={() => handleTagClick(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <textarea
                  placeholder="Comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                ></textarea>
                <button
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={isSubmittingFeedback}
                >
                  {isSubmittingFeedback ? "Submitting..." : "Submit"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      )}
    </>
  );
};

export default MaintenanceDetails;
